import { Box, Button, Typography, useTheme } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import CreateAlertActionButton from './CreateAlertActionButton'
import BacktestActionsMenu from './BacktestActionsMenu'
import TuneIcon from '@mui/icons-material/Tune'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import { useModalContext } from '../../contexts/ModalProvider'
import { useBacktestContext } from '../../app/backtest/contexts/BacktestContext'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../../contexts/SnackbarHelperContext'
import LockIcon from '@mui/icons-material/Lock'
import _cloneDeep from 'lodash.clonedeep'
import { normalizeSchedule, convertScheduleTz } from '../../common/backtestSchedule'
import { getClientTimeZone } from '../../common/timezones'
import generateShareLink from '../../common/utils/generateShareLink'
import ShareDialog from '../../app/builder/components/sidebar/patternInput/ShareDialog'
import { firebase_functions } from '../../common/firebaseConfig'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import ConfirmDialog from '../../app/builder/components/sidebar/patternInput/ConfirmDialog'
import { BacktestFolder } from '../../hooks/useBacktestFolders'

function SettingsActionBar({
  backtest,
  canView,
  onView,
  goBack,
  folders,
  onMoveToFolder,
}: {
  backtest: any
  canView?: boolean
  onView?: () => void
  goBack: () => void
  folders?: BacktestFolder[]
  onMoveToFolder?: (folderId: string | null) => void | Promise<void>
}) {
  const theme = useTheme()

  const { usermeta, canShowcaseViewBacktestSettings } = useApplicationSuiteContext()

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const { setBacktestSettingInputData } = useBacktestContext()

  const { setIsBacktestRunDialogOpen } = useModalContext()

  const [hideBacktestCallable, hideBacktestLoading] = useHttpsCallable(
    firebase_functions,
    'hideBacktest'
  )

  const [updateBacktestVisibilityCallable, updateVisibilityLoading] = useHttpsCallable(
    firebase_functions,
    'updateBacktestVisibility'
  )

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [shareable, setShareable] = useState<any>(null)
  const [isHideBacktestDialogOpen, setIsHideBacktestDialogOpen] = useState(false)
  const [isPublicOverride, setIsPublicOverride] = useState<boolean | null>(null)

  useEffect(() => {
    setIsPublicOverride(null)
  }, [backtest?.id])

  if (!backtest) {
    return null
  }

  const effectiveBacktest =
    isPublicOverride === null
      ? backtest
      : {
          ...backtest,
          backtestSettings: { ...backtest.backtestSettings, isPublic: isPublicOverride },
        }

  function openBacktestRunDialog() {
    if (!canShowcaseViewBacktestSettings && backtest.gcid !== usermeta.gcid) {
      return
    }

    if (!backtest.backtestSettings.gcid) {
      // migrate the runner as the owner
      backtest.backtestSettings.gcid = backtest.gcid
    }

    const viewerTz = getClientTimeZone()
    const rawSettings = _cloneDeep(backtest.backtestSettings)
    const normalized = normalizeSchedule(rawSettings)
    const converted = convertScheduleTz(
      normalized,
      viewerTz,
      backtest.backtestSettings.dateRange?.startDate
    )
    setBacktestSettingInputData({
      ...rawSettings,
      schedule: converted,
    })
    setIsBacktestRunDialogOpen(true)
  }

  function edit() {
    if (onView && canView) {
      onView()
    }
  }

  async function shareBacktest() {
    const shareable = await generateShareLink(
      backtest.id,
      `usermeta/${backtest.gcid}/backtests/${backtest.id}`,
      'BACKTEST',
      {
        name: backtest.backtestSettings.name,
        createdBy: { gcid: usermeta?.gcid, email: usermeta?.email },
      }
    )
    setShareable(shareable)
    setIsShareDialogOpen(true)
  }

  function handleHideBacktest() {
    setIsHideBacktestDialogOpen(true)
  }

  async function confirmHideBacktest() {
    await hideBacktestCallable({ backtestId: backtest.id })
    setIsHideBacktestDialogOpen(false)
  }

  async function toggleBacktestVisibility(nextIsPublic: boolean) {
    const result = await updateBacktestVisibilityCallable({
      backtestId: backtest.id,
      visibility: nextIsPublic,
    })
    if (!result) {
      setSnackbarSettings({
        message: 'Error updating backtest visibility',
        severity: 'error',
      })
      return
    }
    setIsPublicOverride(nextIsPublic)
    setSnackbarSettings({
      message: 'Backtest updated successfully',
      severity: 'success',
    })
  }

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingTop: '12px',
          paddingBottom: '4px',
          paddingLeft: '16px',
          paddingRight: '16px',
          backgroundColor: theme.palette.background.custom4,
          boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
          boxSizing: 'border-box',
          zIndex: 90,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex' }}>
            <CreateAlertActionButton
              backtest={backtest}
              data-tour-id="create-alert-button-backtest"
            />
            <BacktestActionsMenu
              backtest={effectiveBacktest}
              canView={canView}
              hideBacktestLoading={hideBacktestLoading}
              onShare={shareBacktest}
              onHide={handleHideBacktest}
              onToggleVisibility={toggleBacktestVisibility}
              updateVisibilityLoading={updateVisibilityLoading}
              folders={folders}
              onMoveToFolder={onMoveToFolder}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canView ? (
              <Button
                variant="outlined"
                onClick={edit}
                endIcon={<RemoveRedEyeIcon />}
                data-tour-id="view-backtest-button"
              >
                View
              </Button>
            ) : (
              <Button variant="outlined" onClick={goBack} startIcon={<ArrowBackIcon />}>
                Back
              </Button>
            )}

            <Button
              variant="contained"
              onClick={openBacktestRunDialog}
              endIcon={
                !canShowcaseViewBacktestSettings && backtest.gcid !== usermeta.gcid ? (
                  <LockIcon color={'warning'} />
                ) : (
                  <TuneIcon />
                )
              }
              disabled={!canShowcaseViewBacktestSettings && backtest.gcid !== usermeta.gcid}
              data-tour-id="rerun-backtest-button"
            >
              Rerun
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ textAlign: 'center', marginTop: 1 }}>
            Date ran: {new Date(backtest.startTime).toLocaleDateString('en-US')}
          </Typography>
          <Typography sx={{ textAlign: 'center', marginTop: 1 }}>
            Cost: {(backtest.system.estimatedCost || 0).toFixed(2)}
          </Typography>
        </Box>
      </Box>
      <ShareDialog
        open={!!isShareDialogOpen}
        title={`Share this backtest`}
        onClose={() => setIsShareDialogOpen(false)}
        shareable={shareable}
      />
      <ConfirmDialog
        open={isHideBacktestDialogOpen}
        title="Hide Backtest"
        description="Once hidden, you will no longer see this backtest. This only effects you, all public backtests in showcase will remain viewable by others."
        onClose={() => setIsHideBacktestDialogOpen(false)}
        onConfirm={confirmHideBacktest}
        confirmButtonText="Hide"
        cancelButtonText="Cancel"
      />
    </>
  )
}

export default SettingsActionBar
